import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useNotification } from '../../Notification/useNotification';
import DefaultImage from '../../../../../app/javascript/assets/missing-file-img-thumb.png';
import LoadingComponent from '../../LoadingComponent/LoadingComponent.jsx';
import { uploadFile } from '../../AwsS3/ReactS3Client';
import { useGetAwsData } from '../hooks/useGetAwsData';
import {
  MAX_ATTACHMENT_SIZE,
  SUCCESS_NO_CONTENT,
  SPECIAL_CHARACTER_REGEX,
} from '../data';

const AddAttachment = ({ addAttachmentHandler }) => {
  const { showError } = useNotification();
  const awsAttachmentData = useGetAwsData({ origin: 'attachments' });
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const { t } = useTranslation('tileForm');

  const initAttachmentUploader = () => {
    fileInputRef.current.click();
  };

  const handleAwsFileInput = e => {
    if (e.currentTarget.files[0].size > MAX_ATTACHMENT_SIZE) {
      showError({ content: t('attachment.fileTooLarge') });
      return;
    }
    setIsUploading(true);
    e.preventDefault();
    const file = e.currentTarget.files[0];
    const fileName = file.name;
    const newFilename = fileName.replace(SPECIAL_CHARACTER_REGEX, '');

    uploadFile({
      ...awsAttachmentData,
      file,
      fileName: newFilename,
      key: `${awsAttachmentData.key}/${newFilename}`,
    }).then(data => {
      if (data.status === SUCCESS_NO_CONTENT) {
        addAttachmentHandler(newFilename, data.location);
      } else {
        showError({ content: t('attachment.error') });
      }
      setIsUploading(false);
    });
  };

  return (
    <AttachmentContainer onClick={initAttachmentUploader}>
      {isUploading ? (
        <LoadingComponent />
      ) : (
        <AttachmentBox className="js-file-upload-trigger">
          <AttachmentImage></AttachmentImage>
          <AttachmentTitle>
            <AttachmentText>{t('attachment.addAttachment')}</AttachmentText>
          </AttachmentTitle>
        </AttachmentBox>
      )}
      <AttachmentInput
        ref={fileInputRef}
        type="file"
        onChange={handleAwsFileInput}
        id="attachments"
        name="attachments"
      />
    </AttachmentContainer>
  );
};

AddAttachment.propTypes = {
  addAttachmentHandler: PropTypes.func,
};

export default AddAttachment;

const AttachmentInput = styled.input`
  display: none;
`;

const AttachmentContainer = styled.div`
  width: 100%;
  ${themeGet('mediaQueries.forMediumUp')} {
    position: relative;
    width: 48%;
  }
  order: 99;
  padding-bottom: 16px;
`;

const AttachmentBox = styled.a`
  display: flex;
  border-radius: 8px;
  border: solid 1px ${themeGet('colors.black.10')};
  margin: 10px 0;
  &:hover {
    box-shadow: 0px 8px 20px 0px ${themeGet('colors.black.10')};
  }
  @media (max-width: 400px) {
    max-width: 200px;
  }
`;

const AttachmentImage = styled.div`
  height: 84px;
  min-width: 84px;
  margin: 8px;
  margin-right: 14px;
  border-radius: 8px;
  background-size: cover;
  background-position: center;
  background-image: url(${DefaultImage});
`;

const AttachmentTitle = styled.div`
  margin-top: 42px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 8px;
  max-width: 300px;
`;

const AttachmentText = styled.span`
  color: ${themeGet('colors.primary.100')};
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0.1px;
`;
